import { supabase } from '@/lib/supabase/client';
import type { ModelVersion } from '@/lib/supabase/types';

/**
 * Get all versions for a model
 */
export async function getVersionsByModelId(
  modelId: string,
): Promise<ModelVersion[]> {
  const { data, error } = await supabase
    .from('model_versions')
    .select('*')
    .eq('model_id', modelId)
    .order('version_number', { ascending: false });

  if (error) {
    console.error('Error fetching versions:', error);
    throw new Error(error.message);
  }

  return data || [];
}

/**
 * Get a specific version by ID
 */
export async function getVersionById(
  versionId: string,
): Promise<ModelVersion | null> {
  const { data, error } = await supabase
    .from('model_versions')
    .select('*')
    .eq('id', versionId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    console.error('Error fetching version:', error);
    throw new Error(error.message);
  }

  return data;
}

/**
 * Get a specific version by model ID and version number
 */
export async function getVersionByNumber(
  modelId: string,
  versionNumber: number,
): Promise<ModelVersion | null> {
  const { data, error } = await supabase
    .from('model_versions')
    .select('*')
    .eq('model_id', modelId)
    .eq('version_number', versionNumber)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    console.error('Error fetching version:', error);
    throw new Error(error.message);
  }

  return data;
}

/**
 * Create a new version (usually called through model update)
 */
export async function createVersion(
  modelId: string,
  scadCode: string,
  commitMessage?: string,
): Promise<ModelVersion> {
  // Get current version count
  const { count } = await supabase
    .from('model_versions')
    .select('*', { count: 'exact', head: true })
    .eq('model_id', modelId);

  const versionNumber = (count || 0) + 1;

  const { data, error } = await supabase
    .from('model_versions')
    .insert({
      model_id: modelId,
      version_number: versionNumber,
      scad_code: scadCode,
      commit_message: commitMessage || null,
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating version:', error);
    throw new Error(error.message);
  }

  // Update model's current_version_id
  await supabase
    .from('models')
    .update({ current_version_id: data.id })
    .eq('id', modelId);

  return data;
}

/**
 * Restore a model to a previous version
 * Creates a new version with the old code
 */
export async function restoreVersion(
  modelId: string,
  versionToRestore: ModelVersion,
): Promise<ModelVersion> {
  return createVersion(
    modelId,
    versionToRestore.scad_code,
    `Restored from version ${versionToRestore.version_number}`,
  );
}

/**
 * Get version count for a model
 */
export async function getVersionCount(modelId: string): Promise<number> {
  const { count, error } = await supabase
    .from('model_versions')
    .select('*', { count: 'exact', head: true })
    .eq('model_id', modelId);

  if (error) {
    console.error('Error getting version count:', error);
    return 0;
  }

  return count || 0;
}
